import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import ApiService from '../../services/apiService';
import { Filters } from '../../interfaces/filters';
import { AreaBalanceDetails, ChartBalance } from '../../interfaces/services/areaBalance';
import { getTTCBaseUrl } from '../../utils/environmentUtils';
import { ApiStatus, DayOfWeek } from '../../utils/enums';

export interface AreaBalanceDetailsState {
    areaBalanceDetailsApiStatus: ApiStatus;
    areaBalanceDetails: AreaBalanceDetails;
}

const initialState: AreaBalanceDetailsState = {
    areaBalanceDetailsApiStatus: ApiStatus.Idle,
    areaBalanceDetails: {
        service_area_id: '',
        service_area_name: '',
        service_area_latitude_value: 0,
        service_area_longitude_value: 0,
        service_area_freight_order_total_count: 0,
        inbound_freight_order_total_count: 0,
        outbound_freight_order_total_count: 0,
        average_daily_balance_amount: 0,
        day_of_week_balance: [],
        daily_balance: [],
        daily_balance_chart_data: [],
        hourly_balance_chart_data: {
            Sunday: [],
            Monday: [],
            Tuesday: [],
            Wednesday: [],
            Thursday: [],
            Friday: [],
            Saturday: [],
        },
    },
};

export const fetchAreaBalanceDetails = createAsyncThunk(
    'areaBalance/fetchAreaBalanceDetails',
    async ({
        service_area_id,
        filter_criteria,
    }: {
        service_area_id: string;
        filter_criteria?: Filters;
    }) => {
        try {
            const { data } = await ApiService.post({
                url: `${getTTCBaseUrl()}/planning/v2/insights/network/area-balance-details`,
                body: {
                    service_area_id,
                    filter_criteria,
                },
            }) as AxiosResponse<AreaBalanceDetails>;

            return data;
        } catch (error) {
            console.error('Unable to load area balance details', error);
            toast.error(`Unable to load area balance details for ${service_area_id || 'area'}`);
            throw error;
        }
    }
);

const areaBalanceDetailsSlice = createSlice({
    name: 'areaBalance',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchAreaBalanceDetails.pending, (state) => {
            state.areaBalanceDetailsApiStatus = ApiStatus.Loading;
        });
        builder.addCase(fetchAreaBalanceDetails.fulfilled, (state, action) => {
            const dailyChartData = action.payload.daily_balance.map((item): ChartBalance => {
                return {
                    ...item,
                    balance_timestamp: item.area_balance_timestamp,
                    inbound_freight_order_count: item.daily_inbound_freight_order_count,
                    outbound_freight_order_count: item.daily_outbound_freight_order_count,
                    balance_order_count: item.daily_inbound_freight_order_count - item.daily_outbound_freight_order_count,
                };
            });

            const hourlyData = [
                '12 AM',
                '1 AM',
                '2 AM',
                '3 AM',
                '4 AM',
                '5 AM',
                '6 AM',
                '7 AM',
                '8 AM',
                '9 AM',
                '10 AM',
                '11 AM',
                '12 PM',
                '1 PM',
                '2 PM',
                '3 PM',
                '4 PM',
                '5 PM',
                '6 PM',
                '7 PM',
                '8 PM',
                '9 PM',
                '10 PM',
                '11 PM',
            ];

            const hourlyChartData = action.payload.day_of_week_balance.reduce((accumulator: { [k in DayOfWeek]: ChartBalance[]; }, currentItem):
                {
                    [k in DayOfWeek]: ChartBalance[];
                } => {
                const chartData = hourlyData.map((hour, index): ChartBalance => {
                    return {
                        balance_timestamp: hour,
                        inbound_freight_order_count: currentItem.day_hourly_inbound_freight_order_average_count[index],
                        outbound_freight_order_count: currentItem.day_hourly_outbound_freight_order_average_count[index],
                        balance_order_count: Number((currentItem.day_hourly_inbound_freight_order_average_count[index] - currentItem.day_hourly_outbound_freight_order_average_count[index]).toFixed(2)),
                    };
                });

                return {
                    ...accumulator,
                    [currentItem.day_of_week_name]: chartData || [],
                };
            }, {
                Sunday: [],
                Monday: [],
                Tuesday: [],
                Wednesday: [],
                Thursday: [],
                Friday: [],
                Saturday: [],
            });

            state.areaBalanceDetailsApiStatus = ApiStatus.Success;
            state.areaBalanceDetails = {
                ...action.payload,
                daily_balance_chart_data: dailyChartData,
                hourly_balance_chart_data: hourlyChartData,
            };
        });
        builder.addCase(fetchAreaBalanceDetails.rejected, (state) => {
            state.areaBalanceDetailsApiStatus = ApiStatus.Failure;
            state.areaBalanceDetails = initialState.areaBalanceDetails;
        });
    },
});

export default areaBalanceDetailsSlice.reducer;
